/**
 * App chrome: sidebar on desktop, bottom tab bar on mobile, and the floating
 * add button that is the entry point for three-tap entry.
 */
import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight,
  BarChart3,
  CalendarClock,
  LayoutDashboard,
  ListOrdered,
  LogOut,
  Moon,
  Plus,
  Settings,
  Sun,
  Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/lib/theme';
import { QuickAddSheet } from '@/components/QuickAddSheet';
import { MovementDialog } from '@/components/MovementDialog';
import { CommandPalette } from '@/components/CommandPalette';

const NAV = [
  { to: '/', label: 'Начало', icon: LayoutDashboard, end: true },
  { to: '/transactions', label: 'Операции', icon: ListOrdered, end: false },
  { to: '/plan', label: 'План', icon: CalendarClock, end: false },
  { to: '/reports', label: 'Справки', icon: BarChart3, end: false },
  { to: '/settings', label: 'Настройки', icon: Settings, end: false },
] as const;

export function AppShell(): JSX.Element {
  const [quickAdd, setQuickAdd] = useState(false);
  const [movement, setMovement] = useState<'transfer' | 'exchange' | 'loan' | null>(null);
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-dvh bg-background">
      {/* ── Desktop sidebar ── */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r bg-card md:flex">
        <div className="flex h-16 items-center gap-2 border-b px-5">
          <Wallet className="h-5 w-5 text-primary" />
          <span className="font-semibold tracking-tight">Каси</span>
        </div>

        <nav className="flex-1 space-y-1 p-3">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-2 border-t p-3">
          <Button className="w-full justify-start gap-2" onClick={() => setQuickAdd(true)}>
            <Plus className="h-4 w-4" />
            Нова операция
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={() => setMovement('transfer')}
          >
            <ArrowLeftRight className="h-4 w-4" />
            Прехвърляне
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-2 text-muted-foreground">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
                  {user?.name?.[0]?.toUpperCase() ?? '?'}
                </span>
                <span className="truncate">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={toggle}>
                {theme === 'dark' ? <Sun /> : <Moon />}
                {theme === 'dark' ? 'Светла тема' : 'Тъмна тема'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem destructive onSelect={() => void handleLogout()}>
                <LogOut />
                Изход
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* ── Mobile header ── */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b bg-background/95 px-4 backdrop-blur md:hidden">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-primary" />
          <span className="font-semibold tracking-tight">Каси</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon-sm" onClick={toggle} aria-label="Смени темата">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => void handleLogout()}
            aria-label="Изход"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* ── Content ── */}
      <main className="md:pl-60">
        {/* Bottom padding clears the tab bar and the floating button on mobile. */}
        <div className="mx-auto w-full max-w-6xl px-4 py-4 pb-32 sm:px-6 md:py-8 md:pb-8">
          <Outlet />
        </div>
      </main>

      {/* ── Mobile: floating add + tab bar ── */}
      <button
        type="button"
        onClick={() => setQuickAdd(true)}
        aria-label="Нова операция"
        className="fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform active:scale-95 md:hidden"
      >
        <Plus className="h-6 w-6" />
      </button>

      <nav className="fixed inset-x-0 bottom-0 z-30 flex h-[calc(4rem+env(safe-area-inset-bottom))] items-start border-t bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                'flex h-16 flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )
            }
          >
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </nav>

      <QuickAddSheet open={quickAdd} onOpenChange={setQuickAdd} />
      <MovementDialog mode={movement} onClose={() => setMovement(null)} />
      <CommandPalette onQuickAdd={() => setQuickAdd(true)} onMovement={setMovement} />
    </div>
  );
}
